/**
 * Admin → Members. Manage who has access to this tenant.
 *
 * MVP scope:
 *   - List members with role + assigned skills
 *   - Add by email (must already have a Bookaburra account)
 *   - Change role (owner/manager/staff) with last-owner guard
 *   - Assign/unassign skills per member (rounds off Capabilities)
 *   - Remove member
 *
 * Cold-start invites (send-signup-then-auto-attach) live behind a
 * future invite-token table.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@bookaburra/ui';
import { Plus, Trash2, UserCog } from 'lucide-react';
import {
  MembersApiError,
  STAFF_TYPE_LABELS,
  addMember,
  addUnavailablePostcode,
  fetchInvites,
  fetchMembers,
  removeMember,
  removeUnavailablePostcode,
  revokeInvite,
  updateMember,
  type ApiMember,
  type ApiUnavailablePostcode,
  type MemberRole,
  type PendingInvite,
  type StaffType,
} from '../booking-flow/members-api';
import { fetchCapabilities } from '../booking-flow/capabilities-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';
import { formatRole } from '../lib/format-role';
import { RolesExplainerCard } from '../components/RolesExplainerCard';

export const Route = createFileRoute('/admin/$slug/members')({
  component: MembersPage,
});

function MembersPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const membersQ = useQuery({
    queryKey: ['admin-members', slug],
    queryFn: () => fetchMembers(slug),
  });
  const invitesQ = useQuery({
    queryKey: ['admin-invites', slug],
    queryFn: () => fetchInvites(slug),
  });
  const capsQ = useQuery({
    queryKey: ['admin-capabilities', slug],
    queryFn: () => fetchCapabilities(slug),
  });
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Team · ${tenantQ.data.tenant.name} Admin` : 'Team',
  );

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admin-members', slug] });
  }
  function invalidateInvites() {
    qc.invalidateQueries({ queryKey: ['admin-invites', slug] });
  }
  function invalidateCaps() {
    qc.invalidateQueries({ queryKey: ['admin-capabilities', slug] });
  }

  const revokeInviteMut = useMutation({
    mutationFn: (tokenHash: string) => revokeInvite(slug, tokenHash),
    onSuccess: invalidateInvites,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<ApiMember | null>(null);

  const removeMut = useMutation({
    mutationFn: (id: string) => removeMember(slug, id),
    onSuccess: invalidate,
  });

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
      <RolesExplainerCard slug={slug} className="mb-6" />

      {membersQ.isLoading ? <Skeleton /> : null}
      {membersQ.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load members</AlertTitle>
          <AlertDescription>{(membersQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {removeMut.error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Couldn&rsquo;t remove member</AlertTitle>
          <AlertDescription>
            {friendlyMembersError(
              removeMut.error instanceof MembersApiError
                ? removeMut.error.code
                : (removeMut.error as Error).message,
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-8">
        {membersQ.data ? (
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Members</h2>
                <p className="text-xs text-muted-foreground">
                  Everyone with login access to this workspace.
                </p>
              </div>
              <Button
                leadingIcon={<Plus className="h-4 w-4" />}
                onClick={() => setAddOpen(true)}
              >
                Add member
              </Button>
            </div>
            <ul className="space-y-2">
              {membersQ.data.members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  skillNameById={
                    new Map((capsQ.data?.skills ?? []).map((s) => [s.id, s.name]))
                  }
                  onEdit={() => setEditing(m)}
                  onRemove={() => {
                    if (
                      confirm(
                        `Remove ${m.name || m.email} from this workspace? They'll no longer be able to sign in.`,
                      )
                    ) {
                      removeMut.mutate(m.id);
                    }
                  }}
                  removing={removeMut.isPending && removeMut.variables === m.id}
                />
              ))}
            </ul>
          </Card>
        ) : null}

        {invitesQ.data && invitesQ.data.invites.length > 0 ? (
          <Card className="p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold">
                Pending invites ({invitesQ.data.invites.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                Email invites that haven&rsquo;t been accepted yet. They expire
                after 7 days. Revoke any you sent by mistake.
              </p>
            </div>
            <ul className="space-y-2">
              {invitesQ.data.invites.map((inv) => (
                <PendingInviteRow
                  key={inv.tokenHash}
                  invite={inv}
                  onRevoke={() => revokeInviteMut.mutate(inv.tokenHash)}
                  revoking={
                    revokeInviteMut.isPending && revokeInviteMut.variables === inv.tokenHash
                  }
                />
              ))}
            </ul>
            {revokeInviteMut.error ? (
              <Alert variant="destructive" className="mt-3">
                <AlertTitle>Couldn&rsquo;t revoke invite</AlertTitle>
                <AlertDescription>
                  {friendlyMembersError(
                    revokeInviteMut.error instanceof MembersApiError
                      ? revokeInviteMut.error.code
                      : (revokeInviteMut.error as Error).message,
                  )}
                </AlertDescription>
              </Alert>
            ) : null}
          </Card>
        ) : null}
      </div>

      <AddMemberDialog
        slug={slug}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => {
          setAddOpen(false);
          invalidate();
          invalidateInvites();
        }}
      />
      <EditMemberDialog
        slug={slug}
        member={editing}
        // Hide archived skills from the assignment grid — they can't
        // be applied to new staff, only restored from the Skills page.
        skills={(capsQ.data?.skills ?? []).filter((s) => s.archivedAt == null)}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          invalidate();
        }}
        // Triggered by the "Won't service" inline list — refetches the
        // members payload so the entry list re-renders without closing
        // the dialog.
        onListChanged={() => {
          invalidate();
        }}
      />

    </main>
  );
}

function MemberRow({
  member,
  skillNameById,
  onEdit,
  onRemove,
  removing,
}: {
  member: ApiMember;
  skillNameById: Map<string, string>;
  onEdit: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-sage/15 bg-card px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{member.name || member.email}</p>
        <p className="text-xs text-muted-foreground">{member.email}</p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
            {formatRole(member.role)}
          </Badge>
          <Badge variant="outline" title="Allocator cost-tier">
            {STAFF_TYPE_LABELS[member.staffType]}
          </Badge>
          {member.skills.map((s) => (
            <Badge key={s.skillId} variant="outline">
              {skillNameById.get(s.skillId) ?? s.skillId}
            </Badge>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<UserCog className="h-4 w-4" />}
          onClick={onEdit}
        >
          Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Remove ${member.name || member.email}`}
          onClick={onRemove}
          disabled={removing}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function AddMemberDialog({
  slug,
  open,
  onClose,
  onSaved,
}: {
  slug: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('staff');
  // After a successful add, show a confirmation panel for a beat so
  // the admin sees whether the email got an invite or the person was
  // attached directly. Click Done to close.
  const [result, setResult] = useState<
    | { kind: 'attached'; name: string; email: string }
    | { kind: 'invited'; email: string }
    | null
  >(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setRole('staff');
      setResult(null);
    }
  }, [open]);

  const saveMut = useMutation({
    mutationFn: () => addMember(slug, { email: email.trim(), role }),
    onSuccess: (data) => {
      if (data.kind === 'attached') {
        setResult({
          kind: 'attached',
          name: data.member.name || data.member.email,
          email: data.member.email,
        });
      } else {
        setResult({ kind: 'invited', email: data.invite.email });
      }
      // Don't close yet — let the admin read the confirmation.
    },
  });

  const canSubmit = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && !saveMut.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    saveMut.mutate();
  }

  function done() {
    if (result) onSaved();
    else onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && done()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
          <DialogDescription>
            If they already have a Bookaburra account, they&rsquo;re added
            straight away. If not, we&rsquo;ll email them an invite link
            (valid for 7 days).
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <Alert variant="info">
              <AlertTitle>
                {result.kind === 'attached'
                  ? `Added ${result.name}`
                  : `Invite sent to ${result.email}`}
              </AlertTitle>
              <AlertDescription>
                {result.kind === 'attached'
                  ? 'They now have access to this workspace. You can edit their role and skills from the members list.'
                  : 'They\u2019ll get an email with a link to set up their account and join. The invite shows up under \u201cPending invites\u201d until they accept.'}
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button type="button" onClick={done}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : null}

        {!result ? (
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField label="Email" required>
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                invalid={invalid}
                aria-describedby={describedBy}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            )}
          </FormField>
          <FormField label="Role" required>
            {({ id }) => (
              <select
                id={id}
                value={role}
                onChange={(e) => setRole(e.target.value as MemberRole)}
                className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
              >
                <option value="staff">Staff — fulfils bookings</option>
                <option value="manager">Manager — manages services + members</option>
                <option value="owner">Owner — full control</option>
              </select>
            )}
          </FormField>
          {saveMut.error ? (
            saveMut.error instanceof MembersApiError &&
            saveMut.error.code === 'plan_limit_reached' ? (
              <PlanLimitNotice
                slug={slug}
                detail={saveMut.error.detail ?? {}}
              />
            ) : (
              <Alert variant="destructive">
                <AlertTitle>Couldn&rsquo;t add</AlertTitle>
                <AlertDescription>
                  {friendlyMembersError(
                    saveMut.error instanceof MembersApiError
                      ? saveMut.error.code
                      : (saveMut.error as Error).message,
                  )}
                </AlertDescription>
              </Alert>
            )
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={saveMut.isPending} disabled={!canSubmit}>
              Add member
            </Button>
          </DialogFooter>
        </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Temporary stub for the plan-limit upgrade prompt. The richer
 * component is being added in a parallel branch (see /admin/<slug>/billing);
 * leaving this stub here so the file typechecks until that lands.
 */
function PlanLimitNotice({
  slug,
  detail,
}: {
  slug: string;
  detail: { limit?: number; current?: number; planSlug?: string };
}) {
  return (
    <Alert variant="warning">
      <AlertTitle>Team limit reached</AlertTitle>
      <AlertDescription>
        {detail.current ?? '\u2014'} of {detail.limit ?? '\u2014'} seats on the{' '}
        <strong>{detail.planSlug ?? 'current'}</strong> plan. Upgrade on the{' '}
        <a
          href={`/admin/${slug}/billing`}
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          Billing page
        </a>
        {' '}to add more.
      </AlertDescription>
    </Alert>
  );
}

function PendingInviteRow({
  invite,
  onRevoke,
  revoking,
}: {
  invite: PendingInvite;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const expiresIn = relativeExpiry(invite.expiresAt);
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-brand-sage/15 bg-card/60 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{invite.email}</p>
        <p className="text-xs text-muted-foreground">
          Role: {formatRole(invite.role)} &middot; {expiresIn}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRevoke}
        disabled={revoking}
      >
        Revoke
      </Button>
    </li>
  );
}

function relativeExpiry(expiresAt: number): string {
  const diff = expiresAt - Date.now();
  if (diff <= 0) return 'expired';
  const days = Math.round(diff / 86_400_000);
  if (days >= 1) return `expires in ${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.max(1, Math.round(diff / 3_600_000));
  return `expires in ${hours}h`;
}

function EditMemberDialog({
  slug,
  member,
  skills,
  onClose,
  onSaved,
  onListChanged,
}: {
  slug: string;
  member: ApiMember | null;
  skills: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
  onListChanged: () => void;
}) {
  const open = member !== null;
  const [role, setRole] = useState<MemberRole>(member?.role ?? 'staff');
  const [staffType, setStaffType] = useState<StaffType>(
    member?.staffType ?? 'full_time',
  );
  const [homePostcode, setHomePostcode] = useState<string>(member?.homePostcode ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(member?.skills.map((s) => s.skillId) ?? []),
  );

  useEffect(() => {
    if (member) {
      setRole(member.role);
      setStaffType(member.staffType);
      setHomePostcode(member.homePostcode ?? '');
      setSelected(new Set(member.skills.map((s) => s.skillId)));
    }
  }, [member]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateMember(slug, member!.id, {
        role,
        staffType,
        homePostcode: homePostcode.trim() === '' ? null : homePostcode.trim(),
        skillIds: [...selected],
      }),
    onSuccess: onSaved,
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!member || saveMut.isPending) return;
    saveMut.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {member ? (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit member</DialogTitle>
            <DialogDescription>{member.name || member.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Role" required>
              {({ id }) => (
                <select
                  id={id}
                  value={role}
                  onChange={(e) => setRole(e.target.value as MemberRole)}
                  className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="owner">Owner</option>
                </select>
              )}
            </FormField>

            <FormField
              label="Staff type"
              description="Used to allocate staff based on service preferences (e.g. allocate full-time staff first)."
            >
              {({ id }) => (
                <select
                  id={id}
                  value={staffType}
                  onChange={(e) => setStaffType(e.target.value as StaffType)}
                  className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
                >
                  <option value="full_time">
                    {STAFF_TYPE_LABELS.full_time} — salaried
                  </option>
                  <option value="part_time">
                    {STAFF_TYPE_LABELS.part_time} — salaried (lower hours)
                  </option>
                  <option value="paid_per_service">
                    {STAFF_TYPE_LABELS.paid_per_service} — contractor, per booking
                  </option>
                  <option value="paid_hourly">
                    {STAFF_TYPE_LABELS.paid_hourly} — no guaranteed shift
                  </option>
                </select>
              )}
            </FormField>

            <FormField
              label="Home postcode"
              description="Used by the 'pick nearest staff' allocator strategy for on-site bookings. Leave blank if not applicable."
            >
              {({ id }) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="e.g. 3000"
                  value={homePostcode}
                  onChange={(e) => setHomePostcode(e.target.value)}
                  maxLength={10}
                />
              )}
            </FormField>

            <UnavailablePostcodesSection
              slug={slug}
              userId={member.id}
              entries={member.unavailablePostcodes ?? []}
              onMutated={onListChanged}
            />

            <fieldset>
              <legend className="text-sm font-medium">Skills</legend>
              {skills.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  No skills defined yet. Add some on the Capabilities page first.
                </p>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {skills.map((s) => (
                    <label
                      key={s.id}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-brand-sage/15 px-3 py-2 text-sm hover:bg-secondary/50"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(s.id)}
                        onChange={() => toggle(s.id)}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                      />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>

            {saveMut.error ? (
              <Alert variant="destructive">
                <AlertTitle>Couldn&rsquo;t save</AlertTitle>
                <AlertDescription>
                  {friendlyMembersError(
                    saveMut.error instanceof MembersApiError
                      ? saveMut.error.code
                      : (saveMut.error as Error).message,
                  )}
                </AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={saveMut.isPending}>
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

/**
 * Inline editor for the per-staff "won't service" postcode list
 * (ADR-0014 §5). Add postcode + optional reason; each row has a remove
 * icon. The list lives on the parent ApiMember payload, so we
 * optimistically refetch via `onMutated` after each write.
 */
function UnavailablePostcodesSection({
  slug,
  userId,
  entries,
  onMutated,
}: {
  slug: string;
  userId: string;
  entries: ApiUnavailablePostcode[];
  onMutated: () => void;
}) {
  const [postcode, setPostcode] = useState('');
  const [reason, setReason] = useState('');

  const addMut = useMutation({
    mutationFn: () =>
      addUnavailablePostcode(slug, userId, {
        postcode: postcode.trim(),
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      setPostcode('');
      setReason('');
      onMutated();
    },
  });

  const removeMut = useMutation({
    mutationFn: (pc: string) => removeUnavailablePostcode(slug, userId, pc),
    onSuccess: onMutated,
  });

  const canAdd = postcode.trim().length > 0 && !addMut.isPending;

  function onAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!canAdd) return;
    addMut.mutate();
  }

  return (
    <fieldset className="rounded-md border border-brand-sage/15 bg-card/40 p-3">
      <legend className="px-1 text-sm font-medium">Won&rsquo;t service</legend>
      <p className="mt-1 text-xs text-muted-foreground">
        When this staff has a job allocated, these postcodes are skipped &mdash;
        useful when the &ldquo;as the crow flies&rdquo; distance is misleading
        (across a bay, around a freeway).
      </p>

      {entries.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {entries.map((e) => (
            <li
              key={e.postcode}
              className="flex items-center justify-between gap-3 rounded-md border border-brand-sage/15 bg-background px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <span className="font-mono font-medium">{e.postcode}</span>
                {e.reason ? (
                  <span
                    className="ml-2 text-xs text-muted-foreground"
                    title={e.reason}
                  >
                    &mdash; {e.reason}
                  </span>
                ) : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove ${e.postcode} from won't service`}
                onClick={() => removeMut.mutate(e.postcode)}
                disabled={removeMut.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">No postcodes blocked.</p>
      )}

      <form onSubmit={onAdd} className="mt-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[6rem]">
          <label className="block text-xs font-medium text-foreground">
            Postcode
          </label>
          <Input
            inputMode="numeric"
            placeholder="3000"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            maxLength={10}
          />
        </div>
        <div className="flex-[2] min-w-[10rem]">
          <label className="block text-xs font-medium text-foreground">
            Reason (optional)
          </label>
          <Input
            placeholder="e.g. across the bay"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={100}
          />
        </div>
        <Button type="submit" size="sm" disabled={!canAdd} loading={addMut.isPending}>
          Add
        </Button>
      </form>
      {addMut.error ? (
        <p className="mt-2 text-xs text-destructive">
          Couldn&rsquo;t add &mdash;{' '}
          {addMut.error instanceof MembersApiError
            ? addMut.error.code
            : (addMut.error as Error).message}
        </p>
      ) : null}
    </fieldset>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-20 animate-pulse rounded-lg bg-secondary" />
      ))}
    </div>
  );
}

function friendlyMembersError(code: string): string {
  switch (code) {
    case 'email_required':
      return 'Email is required.';
    case 'role_invalid':
      return 'Pick a valid role: staff, admin, or owner.';
    case 'not_found_or_already_used':
      return 'Invite no longer pending. Refresh the page.';
    case 'already_a_member':
      return "They're already a member of this workspace.";
    case 'only_owner_can_set_owner':
      return 'Only an owner can promote someone to owner.';
    case 'only_owner_can_set_staff_type':
      return 'Only an owner can change a member’s staff type.';
    case 'invalid_staff_type':
      return 'Pick a valid staff type: full time, part time, paid per service, or paid hourly.';
    case 'only_owner_can_remove_owner':
      return 'Only an owner can remove another owner.';
    case 'cannot_remove_self_as_owner':
      return "You can't remove yourself as an owner. Promote someone else to owner first, then ask them to remove you.";
    case 'last_owner':
      return "Can't demote or remove the last owner — promote someone else to owner first.";
    case 'invalid_skill_ids':
      return 'One or more skills no longer exist. Refresh and try again.';
    case 'role_too_low':
      return 'You need admin or owner access to manage members.';
    case 'not_found':
      return 'Member no longer exists. Refresh the page.';
    default:
      return code;
  }
}
